/**
 * Tier 2 Item 24 — Apple Pay / Google Pay via Stripe PaymentRequest API.
 *
 * Shows a native wallet button (Apple Pay on Safari/iOS, Google Pay on
 * Chrome/Android) when supported. On confirm it calls back with a Stripe
 * paymentIntent.id that the checkout flow uses to place the order.
 *
 * Must be rendered inside <Elements>.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useStripe, PaymentRequestButtonElement } from '@stripe/react-stripe-js';

export default function WalletPay({
  amount,            // dollars
  currency = 'cad',
  country = 'CA',
  label = 'Total',
  userId = null,
  enabled = true,
  onSuccess,         // ({ paymentIntentId }) => Promise<void>
  onError,           // (err) => void
}) {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canPay, setCanPay] = useState(false);
  const handlerRef = useRef({ onSuccess, onError });
  handlerRef.current = { onSuccess, onError };

  useEffect(() => {
    if (!enabled || !stripe || !amount || amount <= 0) return;
    const pr = stripe.paymentRequest({
      country,
      currency,
      total: { label, amount: Math.round(amount * 100) },
      requestPayerName: true,
      requestPayerEmail: true,
    });
    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr);
        setCanPay(true);
      }
    }).catch(() => {});

    pr.on('paymentmethod', async (ev) => {
      try {
        // Idempotency key prevents duplicate intents from rapid wallet double-tap.
        const idempotencyKey = `wallet-${userId || 'anon'}-${Math.round(amount * 100)}-${Date.now()}`;
        const intentRes = await fetch('/api/payments/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
          body: JSON.stringify({ amount, currency, userId, source: 'wallet' }),
        });
        const intent = await intentRes.json();
        if (!intentRes.ok) throw new Error(intent.error || 'Payment setup failed');

        const { error: confirmErr, paymentIntent } = await stripe.confirmCardPayment(
          intent.clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmErr) {
          ev.complete('fail');
          handlerRef.current.onError?.(new Error(confirmErr.message));
          return;
        }

        ev.complete('success');

        // Some intents need additional auth even after wallet confirmation
        if (paymentIntent.status === 'requires_action') {
          const { error: actionErr, paymentIntent: pi2 } = await stripe.confirmCardPayment(intent.clientSecret);
          if (actionErr) { handlerRef.current.onError?.(new Error(actionErr.message)); return; }
          await handlerRef.current.onSuccess?.({ paymentIntentId: pi2.id });
        } else if (paymentIntent.status === 'succeeded') {
          await handlerRef.current.onSuccess?.({ paymentIntentId: paymentIntent.id });
        } else {
          handlerRef.current.onError?.(new Error(`Payment status: ${paymentIntent.status}`));
        }
      } catch (err) {
        ev.complete('fail');
        handlerRef.current.onError?.(err);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe, enabled]);

  // Keep total in sync with cart changes
  useEffect(() => {
    if (paymentRequest && amount > 0) {
      paymentRequest.update({
        total: { label, amount: Math.round(amount * 100) },
        currency,
      });
    }
  }, [paymentRequest, amount, currency, label]);

  if (!enabled || !canPay || !paymentRequest) return null;

  return (
    <div style={{ margin: '12px 0' }}>
      <PaymentRequestButtonElement
        options={{
          paymentRequest,
          style: { paymentRequestButton: { theme: 'dark', height: '44px' } },
        }}
      />
      <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 12, margin: '8px 0' }}>
        — or pay with card below —
      </div>
    </div>
  );
}
